// "Busy teams run on ClearMakes" (after Clay's "GTM engineers build on Clay"): a row
// of use cases that moves on by itself, and for each one a picture on a soft blue
// backdrop: the list it works from, the instruction it was given, and the result
// waiting for a person's OK.
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { FileText } from "lucide-react";
import { integrationByKey } from "@/data/site";
import { EASE, Rise, useStill, useTyped } from "./motion";

const INBOX = "/brand/inbox-mark-256.png";
const AGENTS = "/brand/agents-mark-256.png";
const MEET = "/brand/meet-mark-256.png";

interface Case {
  key: string;
  label: string;
  line: string;
  product: { logo: string; name: string };
  list: { title: string; cols: [string, string, string]; rows: [string, string, string][] };
  ask: { title: string; text: string; button: string };
  result: { app?: string; logo?: string; who: string; title: string; body: string; source?: string; primary?: string; secondary?: string };
  glow: [string, string, string];
}

const CASES: Case[] = [
  {
    key: "support",
    label: "Customer support",
    line: "Answer the questions you get every day, and keep every reply in your hands.",
    product: { logo: INBOX, name: "ClearMakes Inbox" },
    list: {
      title: "Support email",
      cols: ["From", "Question", "Status"],
      rows: [
        ["Maya R.", "Is my order on its way?", "Draft ready"],
        ["Northwind", "Do you ship to Canada?", "Draft ready"],
        ["Leo Park", "Can I change my plan?", "Waiting for you"],
        ["Priya Shah", "Refund for order 5561", "Sent"],
        ["Globex", "What are your opening hours?", "Sent"],
      ],
    },
    ask: { title: "Workflow", text: "When a support email arrives, draft a reply from my saved answers and wait for my OK.", button: "Turn on" },
    result: { app: "gmail", who: "ClearMakes Inbox", title: "Reply to Maya R.", body: "Hi Maya, your order left us on Tuesday and should reach you by Friday.", primary: "Approve and send", secondary: "Edit" },
    glow: ["hsl(216 84% 52%)", "hsl(206 90% 68%)", "hsl(222 60% 14%)"],
  },
  {
    key: "sales",
    label: "Sales follow-ups",
    line: "Every new lead lands in HubSpot, and every call ends with the follow-up ready.",
    product: { logo: INBOX, name: "ClearMakes Inbox" },
    list: {
      title: "New leads",
      cols: ["Company", "Came from", "Status"],
      rows: [
        ["Contoso", "Website form", "Added to HubSpot"],
        ["Globex", "Email", "Added to HubSpot"],
        ["Initech", "#sales in Slack", "Added to HubSpot"],
        ["Northwind", "Call with Sam", "Follow-up drafted"],
        ["Umbrella", "Email", "Added to HubSpot"],
      ],
    },
    ask: { title: "Workflow", text: "When someone posts a new lead in #sales, add it to HubSpot and tell the owner.", button: "Turn on" },
    result: { app: "gmail", who: "ClearMakes Inbox", title: "Follow-up to Sam, Northwind", body: "Thanks for today, Sam. Here is the price list for the 30-day pilot we talked about.", primary: "Approve and send", secondary: "Edit" },
    glow: ["hsl(222 80% 42%)", "hsl(216 84% 62%)", "hsl(222 60% 12%)"],
  },
  {
    key: "finance",
    label: "Finance",
    line: "Invoices and sign-offs reach the right channel, already summarised.",
    product: { logo: INBOX, name: "ClearMakes Inbox" },
    list: {
      title: "Finance email",
      cols: ["From", "About", "Status"],
      rows: [
        ["Acme Co", "Invoice #2041", "$4,200, due Fri"],
        ["Leo, Finance", "Q3 numbers", "Sign-off by Wed"],
        ["Dana", "Payment sent", "Done"],
        ["Hooli", "Receipt, September", "Saved to Drive"],
        ["Initech", "Invoice #2044", "$1,150, due Mon"],
      ],
    },
    ask: { title: "Workflow", text: "When an invoice arrives, summarise it and post it to #finance in Slack.", button: "Turn on" },
    result: { app: "slack", who: "ClearMakes Inbox", title: "# finance", body: "Acme Co sent invoice #2041: $4,200, due Friday. Leo, can you approve it?", primary: "Approve", secondary: "Ask a question" },
    glow: ["hsl(216 84% 55%)", "hsl(196 85% 62%)", "hsl(222 55% 14%)"],
  },
  {
    key: "meetings",
    label: "Meetings",
    line: "A short answer while the meeting is still happening, and the notes when it ends.",
    product: { logo: MEET, name: "ClearMeet" },
    list: {
      title: "Meetings",
      cols: ["Meeting", "When", "Notes"],
      rows: [
        ["Weekly pilot review", "Today, 10:00", "Writing now"],
        ["Hiring sync", "Yesterday", "Ready"],
        ["Northwind demo", "Monday", "Ready"],
        ["Board prep", "Friday", "Ready"],
        ["Team lunch plan", "Last week", "Ready"],
      ],
    },
    ask: { title: "Question heard", text: "Sam: What would a pilot cost for forty people?", button: "Answer" },
    result: { logo: MEET, who: "ClearMeet", title: "Say this", body: "At $18 a seat, forty people come to $720 a month. The first 30 days are free.", primary: "Copy", secondary: "Share" },
    glow: ["hsl(230 70% 48%)", "hsl(216 84% 58%)", "hsl(225 60% 11%)"],
  },
  {
    key: "website",
    label: "Website visitors",
    line: "Your website answers visitors day and night, from your own pages and documents.",
    product: { logo: AGENTS, name: "ClearMakes Agents" },
    list: {
      title: "Website chats",
      cols: ["Visitor", "Asked", "Status"],
      rows: [
        ["Toronto", "Do you ship to Canada?", "Answered"],
        ["London", "What are your opening hours?", "Answered"],
        ["Austin", "Is there a free plan?", "Answered"],
        ["Mumbai", "Can I talk to a person?", "Passed to you"],
        ["Berlin", "How do I reset my password?", "Answered"],
      ],
    },
    ask: { title: "Agent", text: "Answer visitors from acme.com and our help documents. Pass anything else to the team.", button: "Put on website" },
    result: { logo: AGENTS, who: "Acme help", title: "Do you ship to Canada?", body: "Yes. Orders to Canada arrive in 5 to 7 working days." },
    glow: ["hsl(206 90% 60%)", "hsl(216 84% 50%)", "hsl(222 60% 14%)"],
  },
  {
    key: "team",
    label: "Team questions",
    line: "Your team asks in plain words and gets the answer from your own documents.",
    product: { logo: AGENTS, name: "ClearMakes Agents" },
    list: {
      title: "What the agent knows",
      cols: ["Source", "Kind", "Status"],
      rows: [
        ["HR handbook.pdf", "Document", "Up to date"],
        ["Operations", "Google Drive folder", "Synced"],
        ["acme.com", "Website, 48 pages", "Up to date"],
        ["Refund policy.pdf", "Document", "Up to date"],
        ["Price list.xlsx", "Document", "Up to date"],
      ],
    },
    ask: { title: "Ask the HR helpdesk", text: "How many weeks of parental leave do we get?", button: "Ask" },
    result: { logo: AGENTS, who: "HR helpdesk", title: "Parental leave", body: "Sixteen weeks, fully paid. You can start up to four weeks before the due date." },
    glow: ["hsl(224 70% 38%)", "hsl(210 90% 68%)", "hsl(222 60% 12%)"],
  },
  {
    key: "ops",
    label: "Operations",
    line: "Work keeps moving by itself: waiting for replies, reminding people, trying again.",
    product: { logo: INBOX, name: "ClearMakes Inbox" },
    list: {
      title: "Workflows",
      cols: ["Workflow", "Runs on", "State"],
      rows: [
        ["Email to Slack", "Gmail, Slack", "On"],
        ["Lead to HubSpot", "Slack, HubSpot", "On"],
        ["FAQ reply", "Gmail", "On"],
        ["Chase unpaid invoice", "Gmail", "Waiting 3 days"],
        ["Meeting notes page", "Notion", "On"],
      ],
    },
    ask: { title: "Workflow", text: "If Acme has not replied in 3 days, send them a friendly reminder.", button: "Turn on" },
    result: { app: "gmail", who: "ClearMakes Inbox", title: "Reminder planned", body: "No reply from Acme yet. A friendly reminder goes out on Wednesday at 9:00.", primary: "OK", secondary: "Change" },
    glow: ["hsl(216 84% 52%)", "hsl(222 75% 32%)", "hsl(222 60% 13%)"],
  },
];

const ROTATE_MS = 6500;

const Stage = ({ c }: { c: Case }) => {
  const still = useStill();
  const [phase, setPhase] = useState(still ? 2 : 0);
  useEffect(() => {
    if (still) return setPhase(2);
    setPhase(0);
    const a = setTimeout(() => setPhase(1), 350);
    return () => clearTimeout(a);
  }, [c.key, still]);
  const typed = useTyped(c.ask.text, phase >= 1, 18);
  useEffect(() => {
    if (!still && phase === 1 && typed.length === c.ask.text.length) {
      const t = setTimeout(() => setPhase(2), 350);
      return () => clearTimeout(t);
    }
  }, [typed, phase, c.ask.text, still]);

  const appLogo = c.result.app ? integrationByKey(c.result.app)?.logo : c.result.logo;

  return (
    <div className="relative mx-auto mt-14 w-full max-w-[1120px] px-2 pb-6 sm:px-10 sm:pb-10">
      {/* the soft blue backdrop and its glow */}
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.div
          key={c.key}
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="absolute -inset-x-2 bottom-0 top-[16%] sm:-inset-x-6"
        >
          <div className="absolute inset-6 rounded-[48px] opacity-70 blur-3xl" style={{ background: `linear-gradient(120deg, ${c.glow[0]}, ${c.glow[1]})` }} />
          <div
            className="absolute inset-0 rounded-[32px] sm:rounded-[40px]"
            style={{ background: `radial-gradient(120% 90% at 15% 100%, ${c.glow[0]}, transparent 60%), radial-gradient(90% 90% at 90% 20%, ${c.glow[1]}, transparent 65%), ${c.glow[2]}` }}
          />
          <div className="cm-grain absolute inset-0 rounded-[32px] opacity-40 mix-blend-soft-light sm:rounded-[40px]" />
        </motion.div>
      </AnimatePresence>

      {/* the list it works from */}
      <div className="relative overflow-hidden rounded-2xl border border-[hsl(0_0%_88%)] bg-background shadow-[0_30px_60px_-30px_hsl(216_60%_20%/0.4)]">
        <div className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-5">
          <span className="flex items-center gap-2 text-[14px] font-semibold text-foreground">
            <img src={c.product.logo} alt="" className="h-5 w-5 brightness-0" /> {c.product.name} <span className="text-border">/</span> {c.list.title}
          </span>
        </div>
        <table className="w-full text-left text-[13.5px] sm:text-[14.5px]">
          <thead>
            <tr className="border-b border-border text-[12.5px] text-muted-foreground">
              {c.list.cols.map((h, i) => (
                <th key={h} scope="col" className={`px-4 py-2.5 font-medium sm:px-5 ${i === 1 ? "hidden sm:table-cell" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {c.list.rows.map((r, i) => (
              <motion.tr
                key={c.key + r[0]}
                initial={still ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.35, ease: EASE }}
                className="border-b border-border last:border-0"
              >
                <td className="px-4 py-3 font-medium text-foreground sm:px-5">{r[0]}</td>
                <td className="hidden px-4 py-3 text-[hsl(0_0%_30%)] sm:table-cell sm:px-5">{r[1]}</td>
                <td className="px-4 py-3 sm:px-5">
                  <span className="rounded-md bg-[hsl(40_9%_95%)] px-2 py-0.5 text-[12.5px] text-foreground">{r[2]}</span>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* the instruction it was given */}
      <motion.div
        key={c.key + "-ask"}
        initial={still ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: EASE }}
        className="relative z-10 -mt-24 w-[min(360px,88%)] rounded-2xl border border-[hsl(0_0%_88%)] bg-background p-4 shadow-[0_30px_60px_-24px_hsl(216_60%_20%/0.5)] sm:-mt-32 sm:ml-[-24px] sm:p-5"
      >
        <p className="text-[15px] font-semibold text-foreground">{c.ask.title}</p>
        <p className="mt-3 min-h-[66px] rounded-xl border border-border bg-[hsl(40_9%_98%)] px-3.5 py-3 text-[14.5px] leading-snug text-foreground">
          {typed}
          {phase === 1 && <span className="cm-caret ml-px inline-block h-[1em] w-[2px] translate-y-[0.15em] bg-foreground" />}
        </p>
        <div className="mt-3 flex justify-end">
          <span className={`rounded-lg px-3.5 py-2 text-[13.5px] font-semibold transition-colors duration-300 ${phase >= 2 ? "bg-[hsl(216_84%_52%)] text-background" : "bg-[hsl(40_9%_93%)] text-muted-foreground"}`}>
            {c.ask.button}
          </span>
        </div>
      </motion.div>

      {/* the result, waiting for a person */}
      <AnimatePresence>
        {phase >= 2 && (
          <motion.div
            key={c.key + "-result"}
            initial={still ? false : { opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="relative z-10 ml-auto mt-4 w-[min(360px,92%)] rounded-2xl border border-[hsl(0_0%_88%)] bg-background p-4 shadow-[0_30px_60px_-24px_hsl(216_60%_20%/0.5)] sm:absolute sm:right-[-12px] sm:top-[-34px] sm:mt-0 sm:p-5"
          >
            <div className="flex items-center gap-2.5">
              {appLogo && <img src={appLogo} alt="" className={`h-7 w-7 shrink-0 rounded-md object-contain ${c.result.app ? "bg-[hsl(40_9%_95%)] p-1 grayscale" : "brightness-0"}`} />}
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold text-foreground">{c.result.who} <span className="font-normal text-muted-foreground">just now</span></p>
                <p className="truncate text-[12.5px] text-muted-foreground">{c.result.title}</p>
              </div>
            </div>
            <p className="mt-3 text-[15px] leading-snug text-foreground">{c.result.body}</p>
            {c.result.source && (
              <p className="mt-2.5 inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-[12px] text-muted-foreground">
                <FileText className="h-3 w-3" /> {c.result.source}
              </p>
            )}
            {c.result.primary && (
              <div className="mt-4 flex gap-2">
                <span className="rounded-lg bg-[hsl(216_84%_52%)] px-3.5 py-2 text-[13.5px] font-semibold text-background">{c.result.primary}</span>
                {c.result.secondary && <span className="rounded-lg border border-border px-3.5 py-2 text-[13.5px] font-medium text-foreground">{c.result.secondary}</span>}
              </div>
            )}
            {!c.result.primary && (
              <p className="mt-3 text-[12.5px] font-medium text-muted-foreground">Answered from your own pages</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const UseCases = () => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tick, setTick] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const row = useRef<HTMLDivElement>(null);
  const inView = useInView(box, { margin: "-120px" });
  const still = useStill();

  useEffect(() => {
    if (still || !inView || paused) return;
    const t = setTimeout(() => setI((v) => (v + 1) % CASES.length), ROTATE_MS);
    return () => clearTimeout(t);
  }, [i, inView, paused, still, tick]);

  // keep the chosen pill in the middle of its row, without moving the page
  useEffect(() => {
    const r = row.current;
    const pill = r?.querySelector<HTMLElement>(`[data-i="${i}"]`);
    if (!r || !pill) return;
    r.scrollTo({ left: pill.offsetLeft - r.clientWidth / 2 + pill.clientWidth / 2, behavior: "smooth" });
  }, [i]);

  const c = CASES[i];

  return (
    <section aria-labelledby="cases-h" className="relative overflow-hidden bg-[hsl(0_0%_4%)] py-24 text-[hsl(0_0%_100%)] sm:py-32">
      <div aria-hidden className="cm-grid-dark pointer-events-none absolute inset-x-0 top-0 h-[600px]" />
      <div ref={box} className="relative mx-auto max-w-[1320px] px-4 sm:px-8">
        <Rise className="text-center">
          <h2 id="cases-h" className="mx-auto max-w-4xl text-[2.6rem] font-semibold leading-[1.02] tracking-[-0.04em] text-[hsl(0_0%_100%)] sm:text-[4.2rem]">
            Busy teams run on <span className="text-[hsl(216_90%_66%)]">ClearMakes</span>
          </h2>
          <div className="relative mx-auto mt-5 h-[3.4em] max-w-2xl text-[1.15rem] leading-snug text-[hsl(0_0%_100%/0.7)] sm:h-[2.8em] sm:text-[1.3rem]">
            <AnimatePresence mode="wait" initial={false}>
              <motion.p key={c.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
                {c.line}
              </motion.p>
            </AnimatePresence>
          </div>
        </Rise>

        <div
          ref={row}
          role="tablist"
          aria-label="What teams use ClearMakes for"
          className="cm-noscroll cm-fade-x relative mx-auto mt-8 flex max-w-[980px] gap-2 overflow-x-auto px-[18%] py-1"
        >
          {CASES.map((t, n) => (
            <button
              key={t.key}
              data-i={n}
              type="button"
              role="tab"
              aria-selected={n === i}
              onClick={() => {
                setI(n);
                setPaused(true);
                setTick((v) => v + 1);
                setTimeout(() => setPaused(false), 12000);
              }}
              className={`relative shrink-0 overflow-hidden rounded-xl px-4 py-2.5 text-[15px] font-semibold transition-colors duration-300 ${
                n === i ? "bg-[hsl(216_84%_52%)] text-[hsl(0_0%_100%)]" : "bg-[hsl(0_0%_100%/0.08)] text-[hsl(0_0%_100%/0.85)] hover:bg-[hsl(0_0%_100%/0.14)]"
              }`}
            >
              {t.label}
              {n === i && !still && !paused && inView && (
                <motion.span
                  key={`${i}-${tick}`}
                  aria-hidden
                  className="absolute bottom-0 left-0 h-[2px] bg-[hsl(0_0%_100%/0.85)]"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: ROTATE_MS / 1000, ease: "linear" }}
                />
              )}
            </button>
          ))}
        </div>

        <div role="tabpanel" aria-label={c.label} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <Stage c={c} />
        </div>
      </div>
    </section>
  );
};
